import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { SpotIcon } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { useCreateProjectAndPushRoute } from ':cloud/hooks/useCreateProjectAndPushRoute';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { ProductItem } from ':cloud/types/ts_types';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';

const CARD_HEIGHT = 276;

/** All products */
export function ProductCard({
  spotIconName,
  title,
  description,
  linkToRoute,
  hasButton,
  productTag,
  createProjectLink,
  productType,
}: ProductItem) {
  const history = useHistory();

  const createProject = useCreateProjectAndPushRoute();

  const handlePress = useCallback(() => {
    if (hasButton) {
      history.push(linkToRoute);
    } else {
      openUrlInNewWindow(linkToRoute);
    }
  }, [hasButton, history, linkToRoute]);

  const handleNewProjectPress = useCallback(() => {
    if (productType === 'waas') {
      logClick(EventName.project_page_start_waas_click);
    } else if (productType === 'staking_api') {
      logClick(EventName.project_page_start_staking_click);
      createProject('staking_api');
      return;
    } else if (productType === 'base') {
      logClick(EventName.project_page_start_base_click);
    } else if (productType === 'waas_consumer') {
      logClick(EventName.project_page_start_waas_consumer_click);
      createProject('waas_consumer');
      return;
    }
    if (createProjectLink) {
      history.push(createProjectLink);
    } else {
      history.push(linkToRoute);
    }
  }, [createProject, createProjectLink, history, linkToRoute, productType]);

  const content = useMemo(() => {
    return (
      <Box bordered borderRadius="roundedLarge" spacing={5} width={357} height={CARD_HEIGHT}>
        <VStack width="100%">
          <VStack alignItems="flex-start" spacingBottom={hasButton ? 5 : 0}>
            <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
              <SpotIcon name={spotIconName} scaleMultiplier={1.25} />
              {productTag ? (
                <HStack gap={1}>
                  {productTag === 'Preview' && <Tag colorScheme="green">Preview</Tag>}
                  {productTag === 'Enterprise' && <Tag colorScheme="gray">Enterprise</Tag>}
                </HStack>
              ) : null}
            </HStack>
            <TextTitle3 as="h1" spacingTop={3}>
              {title}
            </TextTitle3>
            <TextBody as="p" color="foregroundMuted">
              {description}
            </TextBody>
          </VStack>
        </VStack>
      </Box>
    );
  }, [hasButton, spotIconName, productTag, title, description]);

  return (
    <Box spacingBottom={3} spacingEnd={3} position="relative">
      <Pressable onPress={handlePress} background="background">
        {content}
      </Pressable>
      <Box position="absolute" bottom={40} left={20}>
        {hasButton ? (
          <Button compact onPress={handleNewProjectPress}>
            {createProjectLink ? 'Create' : 'Go'}
          </Button>
        ) : (
          <Button variant="secondary" compact onPress={handlePress}>
            Docs
          </Button>
        )}
      </Box>
    </Box>
  );
}
